import type React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Typography,
  CircularProgress,
} from "@mui/material";
import { Visibility } from "@mui/icons-material";
import { Link } from "react-router-dom";
import styles from "./LeaveApplicationTable.module.css";

// Updated interface to match the API response
export interface LeaveApplicationItem {
  id: string;
  leave_type?: string;
  leaveType?: string;
  start_date?: string;
  startDate?: string;
  end_date?: string;
  endDate?: string;
  days_requested?: number;
  daysRequested?: number;
  status: string;
  created_at?: string;
  createdAt?: string;
  department?: string;
  userName?: string;
  email?: string;
  // Additional fields that might be in the mock data
  employeeName?: string;
  employeeId?: string;
  relieverName?: string;
  relieverId?: string;
  leaveReason?: string;
  supportingDocuments?: string[];
  leaveDuration?: string;
  leave_id?: string;
}

interface LeaveApplicationTableProps {
  applications: LeaveApplicationItem[];
  showEmployee?: boolean;
  loading?: boolean;
  error?: string | null;
  usedDays?: number;
  remainingDays?: number;
  totalDays?: number;
}

const LeaveApplicationTable: React.FC<LeaveApplicationTableProps> = ({
  applications,
  showEmployee = false,
  loading = false,
  error = null,
  usedDays,
  remainingDays,
  totalDays,
}) => {
  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusChip = (status: string) => {
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case "approved":
        return <Chip label="Approved" color="success" size="small" />;
      case "rejected":
        return <Chip label="Rejected" color="error" size="small" />;
      case "pending":
        return <Chip label="Pending" color="warning" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <CircularProgress />
        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
          Loading leave applications...
        </Typography>
      </div>
    );
  }

  if (error) {
    return (
      <Paper className={styles.errorState}>
        <Typography variant="body1" color="error">
          {error}
        </Typography>
      </Paper>
    );
  }

  if (applications.length === 0) {
    return (
      <Paper className={styles.emptyState}>
        <Typography variant="body1" color="textSecondary">
          No leave applications found.
        </Typography>
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table aria-label="leave applications table">
        <TableHead>
          <TableRow>
            {showEmployee && (
              <>
                <TableCell>Employee</TableCell>
                <TableCell>Department</TableCell>
              </>
            )}
            <TableCell>Leave Type</TableCell>
            <TableCell>Period</TableCell>
            <TableCell>Days</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Submitted</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {applications.map((application) => (
            <TableRow key={application.id}>
              {showEmployee && (
                <>
                  <TableCell>
                    <div className={styles.employeeCell}>
                      <Typography variant="body2">
                        {application.employeeName ||
                          application.userName ||
                          "N/A"}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {application.employeeId || application.email || "N/A"}
                      </Typography>
                    </div>
                  </TableCell>
                  <TableCell>{application.department || "N/A"}</TableCell>
                </>
              )}
              <TableCell>
                {application.leaveType || application.leave_type || "N/A"}
              </TableCell>
              <TableCell>
                {formatDate(application.startDate || application.start_date)} -{" "}
                {formatDate(application.endDate || application.end_date)}
              </TableCell>
              <TableCell>
                {application.daysRequested ||
                  application.days_requested ||
                  "N/A"}
              </TableCell>
              <TableCell>{getStatusChip(application.status)}</TableCell>
              <TableCell>
                {formatDate(application.createdAt || application.created_at)}
              </TableCell>
              <TableCell align="right">
                <Button
                  component={Link}
                  to={
                    {
                      pathname: `/folder/leave_detailpage`,
                      search: `?id=${application.leave_id}`,
                      state: { application, usedDays, remainingDays, totalDays },
                    } as any
                  } // cast to any to bypass TypeScript complaints
                  startIcon={<Visibility />}
                  size="small"
                >
                  View
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default LeaveApplicationTable;

{
  /* <Button
                  component={Link}
                  to={{
                    pathname: `/folder/leave_detailpage?id=${application.leave_id}`,
                    state: { application, usedDays, remainingDays }, // Pass additional data if needed
                  }}
                  startIcon={<Visibility />}
                  size="small"
                >
                  View
                </Button> */
}
